import { fromEnumAsStringOrNull, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { Language } from './language.js';

export class LocalizationSettings extends DataModel {
  static readonly TYPE_ID: string = '9b1a399859ea455ea395f401fbdab010';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly allowLocalization: boolean,
    public readonly defaultLanguage: Language | null = null,
  ) {
    super();
  }

  get $typeId(): string {
    return LocalizationSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LocalizationSettings.SCHEMA_VERSION);
  }

  static none(): LocalizationSettings {
    return new LocalizationSettings(
        false,
        null,
    );
  }

  static fromMap(context: SerializationContext, map:SerializedData): LocalizationSettings {
    return new LocalizationSettings(
        MapUtils.get<boolean>(map, 'allowLocalization'),
        toEnumFromStringOrNull(MapUtils.getOrNull<string>(map, 'defaultLanguage'), Language),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'allowLocalization': this.allowLocalization,
      'defaultLanguage': fromEnumAsStringOrNull(this.defaultLanguage),
    };
  }

  clone(): this {
    return new LocalizationSettings(
        this.allowLocalization,
        this.defaultLanguage,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.allowLocalization === this.allowLocalization &&
        other.defaultLanguage === this.defaultLanguage;
  }
}

export class LocalizationSettingsBuilder extends DataModelBuilder<LocalizationSettings> {
  build(context: SerializationContext, map: SerializedData): LocalizationSettings {
    return LocalizationSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LocalizationSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LocalizationSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2 adds the [defaultLanguage] field.
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
